import { Plus } from "lucide-react"
import { type ReactNode, useCallback, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"

// A2P 10DLC brand registration. Labels are human-friendly; ops maps them to TCR
// enum values in the carrier submission. Shared by the setup compliance step and
// the dashboard banner so the form stays in one place. On submit it serializes
// its named fields and hands the payload to `onSubmit` (the page POSTs it).

const BUSINESS_TYPES = [
  "Sole Proprietorship",
  "Partnership",
  "Limited Liability Company (LLC)",
  "Private Corporation",
  "Public Corporation",
  "Non-profit",
  "Government",
]

const INDUSTRIES = [
  "Real estate",
  "Home & trade services",
  "Healthcare",
  "Professional services",
  "Retail & e-commerce",
  "Automotive",
  "Financial services",
  "Other",
]

const REG_ID_TYPES = [
  "USA: Employer Identification Number (EIN)",
  "Canada: Business Number (BN)",
  "Other",
]

const REGIONS = [
  "USA and Canada",
  "USA",
  "Canada",
  "Europe",
  "Latin America",
  "Asia Pacific",
  "Africa",
]

const COUNTRIES = ["United States", "Canada"]

const JOB_TITLES = [
  "Director",
  "GM",
  "VP",
  "CEO",
  "CFO",
  "General Counsel",
  "Other",
]

// Fields we can pre-populate from the account's business profile + the current
// user (server-provided). Everything else the business fills in.
export interface A2pPrefill {
  business_name?: string | null
  website?: string | null
  phone?: string | null
  address?: string | null
  rep_first_name?: string | null
  rep_last_name?: string | null
  rep_email?: string | null
}

export function A2pRegistrationSheet({
  open,
  onOpenChange,
  onSubmit,
  prefill = {},
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onSubmit?: (payload: Record<string, string>) => void
  prefill?: A2pPrefill
}) {
  const [companyType, setCompanyType] = useState("")
  const [secondRep, setSecondRep] = useState(false)
  // Gate the submit button on native form validity — every field is `required`
  // (except the optional street line 2), so the carrier submission can't go out
  // half-filled. `required` is the hard gate (the browser blocks an invalid
  // submit); this just mirrors it on the button as it's filled in. Starts
  // disabled and recomputes on every edit.
  const formRef = useRef<HTMLFormElement>(null)
  const [canSubmit, setCanSubmit] = useState(false)
  const updateValidity = useCallback(() => {
    setCanSubmit(formRef.current?.checkValidity() ?? false)
  }, [])

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    const payload: Record<string, string> = {}
    data.forEach((value, key) => {
      if (typeof value === "string") payload[key] = value
    })
    onSubmit?.(payload)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 overflow-y-auto p-0 sm:max-w-lg">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>SMS registration</SheetTitle>
          <p className="text-muted-foreground text-sm">
            Carriers require this to deliver your business texts. We filled in
            what we can from your profile. Add the rest and submit.
          </p>
        </SheetHeader>
        <form
          ref={formRef}
          id="a2p-registration-form"
          onSubmit={handleSubmit}
          onChange={updateValidity}
          className="contents"
        >
          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
            <div className="space-y-4">
              <p className="text-sm font-semibold">Business</p>
              <Field label="Business name">
                <Input
                  name="business_name"
                  defaultValue={prefill.business_name ?? ""}
                  required
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Business type">
                  <NativeSelect
                    name="business_type"
                    options={BUSINESS_TYPES}
                    required
                  />
                </Field>
                <Field label="Industry">
                  <NativeSelect name="industry" options={INDUSTRIES} required />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Registration ID type">
                  <NativeSelect
                    name="registration_id_type"
                    options={REG_ID_TYPES}
                    defaultValue="USA: Employer Identification Number (EIN)"
                    required
                  />
                </Field>
                <Field label="Registration number">
                  <Input
                    name="registration_number"
                    placeholder="12-3456789"
                    required
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Region of operation">
                  <NativeSelect
                    name="region"
                    options={REGIONS}
                    defaultValue="USA and Canada"
                    required
                  />
                </Field>
                <Field label="Company type">
                  <select
                    name="company_type"
                    value={companyType}
                    onChange={(e) => setCompanyType(e.target.value)}
                    required
                    className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                  >
                    <option value="">Select…</option>
                    <option>Private</option>
                    <option>Public</option>
                  </select>
                </Field>
              </div>
              {companyType === "Public" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Stock exchange">
                    <Input
                      name="stock_exchange"
                      placeholder="NASDAQ"
                      required
                    />
                  </Field>
                  <Field label="Ticker symbol">
                    <Input name="ticker" placeholder="ACME" required />
                  </Field>
                </div>
              )}
              <Field label="Website">
                <Input
                  name="website"
                  type="url"
                  defaultValue={prefill.website ?? ""}
                  required
                />
              </Field>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-semibold">Physical address</p>
              <Field label="Street address">
                <Input
                  name="street1"
                  defaultValue={prefill.address ?? ""}
                  required
                />
              </Field>
              <Field label="Street address 2" optional>
                <Input name="street2" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="City">
                  <Input name="city" required />
                </Field>
                <Field label="State or province">
                  <Input name="state" required />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Postal code">
                  <Input name="postal" required />
                </Field>
                <Field label="Country">
                  <NativeSelect
                    name="country"
                    options={COUNTRIES}
                    defaultValue="United States"
                    required
                  />
                </Field>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-semibold">Authorized representative</p>
              <AuthRepFields
                prefix="rep"
                defaults={{
                  firstName: prefill.rep_first_name ?? "",
                  lastName: prefill.rep_last_name ?? "",
                  email: prefill.rep_email ?? "",
                  phone: prefill.phone ?? "",
                }}
                emailNote="This will appear on your SMS policy as your contact email."
              />
            </div>

            {secondRep ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">
                    Second authorized representative
                  </p>
                  <button
                    type="button"
                    onClick={() => setSecondRep(false)}
                    className="text-muted-foreground hover:text-foreground text-sm"
                  >
                    Remove
                  </button>
                </div>
                <AuthRepFields prefix="rep2" />
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setSecondRep(true)}
              >
                <Plus className="size-4" />
                Add 2nd authorized rep
              </Button>
            )}
          </div>
          <SheetFooter className="border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              Submit registration
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

function Field({
  label,
  optional,
  children,
}: {
  label: string
  optional?: boolean
  children: ReactNode
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {optional && (
          <span className="text-muted-foreground font-normal"> (optional)</span>
        )}
      </Label>
      {children}
    </div>
  )
}

function NativeSelect({
  name,
  options,
  defaultValue = "",
  required,
}: {
  name: string
  options: string[]
  defaultValue?: string
  required?: boolean
}) {
  return (
    <select
      name={name}
      defaultValue={defaultValue}
      required={required}
      className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o}>{o}</option>
      ))}
    </select>
  )
}

function AuthRepFields({
  prefix,
  defaults,
  emailNote,
}: {
  prefix: string
  defaults?: {
    firstName: string
    lastName: string
    email: string
    phone: string
  }
  // IMP-411: shown under the primary rep's email — the rep is the SMB owner, and
  // this address is what appears as the contact on the public SMS policy.
  emailNote?: string
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <Input
            name={`${prefix}_first_name`}
            defaultValue={defaults?.firstName}
            required
          />
        </Field>
        <Field label="Last name">
          <Input
            name={`${prefix}_last_name`}
            defaultValue={defaults?.lastName}
            required
          />
        </Field>
      </div>
      <Field label="Email">
        <Input
          name={`${prefix}_email`}
          type="email"
          defaultValue={defaults?.email}
          placeholder="jane@acme.com"
          required
        />
        {emailNote && (
          <p className="text-muted-foreground text-xs">{emailNote}</p>
        )}
      </Field>
      <Field label="Phone">
        <div className="flex gap-2">
          <div className="border-input text-muted-foreground bg-muted/40 flex h-9 items-center rounded-md border px-3 text-sm">
            +1
          </div>
          <Input
            name={`${prefix}_phone`}
            className="flex-1"
            inputMode="tel"
            defaultValue={defaults?.phone}
            placeholder="4151231234"
            required
          />
        </div>
      </Field>
      <Field label="Business title">
        <NativeSelect
          name={`${prefix}_title`}
          options={JOB_TITLES}
          defaultValue="Director"
          required
        />
      </Field>
    </div>
  )
}
